import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CategoryCard from '../components/CategoryCard';
import { fetchCategories, fetchListings, fetchRegions } from '../lib/api';
import { seedCategories, seedListings } from '../data/seed';
import type { Category, Listing } from '../types';

interface RegionCount {
  name: string;
  count: number;
}

export default function Categories() {
  const [categories, setCategories] = useState<Category[]>(seedCategories);
  const [listings, setListings] = useState<Listing[]>(seedListings);
  const [regions, setRegions] = useState<RegionCount[]>([]);

  useEffect(() => {
    fetchCategories().then(setCategories);
    fetchListings().then(setListings);
    fetchRegions().then(setRegions).catch(() => setRegions([]));
  }, []);

  return (
    <div>
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 pt-10 md:pt-16">
        <div className="flex items-center gap-2 text-sm text-text-muted mb-4">
          <Link to="/" className="text-text-secondary hover:text-accent transition-colors">Home</Link>
          <span>/</span>
          <span>Categories</span>
        </div>
        <h1 className="font-display text-xl md:text-[2.5rem] font-bold tracking-tight mb-3">All categories</h1>
        <p className="text-text-secondary max-w-[600px] mb-6 text-sm md:text-base">Explore New Zealand's marine industry by category. Click any category to see listings.</p>
      </div>
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-4">
        <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-[repeat(auto-fill,minmax(220px,1fr))] md:gap-4">
          {categories.map((c) => {
            const count = listings.filter(l => l.category_id === c.id).length;
            return <CategoryCard key={c.id} category={c} count={count} />;
          })}
        </div>
      </div>
      {/* Regions — crawlable cross-links to every NZ region */}
      {regions.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-4 md:px-8 pb-10 md:pb-16" aria-labelledby="regions-heading">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-5 gap-4">
            <div>
              <h2 id="regions-heading" className="font-display text-2xl md:text-[2rem] font-bold tracking-tight">Browse by region</h2>
              <p className="text-text-secondary text-sm mt-1">Marine businesses across every New Zealand region.</p>
            </div>
            <Link to="/regions" className="text-sm font-semibold text-accent inline-flex items-center hover:underline">All regions</Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {regions.map((r) => (
              <Link
                key={r.name}
                to={`/browse?region=${encodeURIComponent(r.name)}`}
                className="px-4 py-2 rounded-full border border-[rgba(16,42,66,0.14)] dark:border-[rgba(214,233,247,0.14)] text-sm text-text-secondary-light dark:text-text-secondary hover:border-accent hover:text-accent transition-colors"
              >
                {r.name} <span className="text-text-muted">({r.count})</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}